import { useState } from "react";
import { IconButton, TextField } from "@mui/material";
import { Box } from "@mui/system";
import SendIcon from "@mui/icons-material/Send";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import axios from "axios";
import styled from "@emotion/styled";
import toast from "react-hot-toast";

interface IMessage {
  attachment?: string;
  isMine: boolean;
  message: string;
  message_id: number;
  sent_time: string;
  user_avatar: string;
  user_name: string;
}

interface ICategory {
  id: number | string;
  name: string;
}

interface IData {
  ticket_id: number | string | Blob;
  category: ICategory;
  title: string;
  sent_time: string;
  messages: IMessage[];
}

const Input = styled("input")({
  display: "none",
});
const Label = styled("label")({
  height: "fit-content",
  "& .MuiIconButton-root": {
    padding: "0",
    width: "30px",
    height: "30px",
    svg: {
      width: "20px",
    },
  },
});

export default function ChatFooter({
  ticket,
  updateTicket,
}: {
  ticket: IData;
  updateTicket: (object: any) => void;
}) {
  const [message, setMessage] = useState("");
  const [attachment, setAttachment] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);

  const sendMessage = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData();

    if (attachment) {
      formData.append("ticket_id", ticket.ticket_id?.toString());
      formData.append("message", message);
      formData.append("attachment", attachment);
    }
    try {
      setLoading(true);
      const { data } = await axios.post(
        "/support-tickets/reply",
        attachment
          ? formData
          : {
            ticket_id: ticket.ticket_id,
            message,
          }
      );
      setMessage("");
      setAttachment(null);

      updateTicket({
        messages: [
          ...ticket.messages,
          {
            ...data.data.message,
            isMine: true,
          },
        ],
      });
      setLoading(false);
    } catch (error: any) {
      toast.error(error.response.data.message || "Something went wrong, check the console");

      setLoading(false);
    }
  };

  const attachFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file || !file.type.includes("image") || file.type === "image/gif") {
      return toast.error(
        "Please select an image file (jpg, jpeg or png)"
      )
    }
    if (file.size > 5 * 1024 * 1024) {
      return toast.error(
        "Please select an image file with size less than 5MB"
      )
    }

    setAttachment(file);
  };

  return (
    <form
      style={{ padding: "8px", borderTop: "1px solid #e0e0e0" }}
      onSubmit={sendMessage}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
        <TextField
          sx={{ flex: 12 }}
          id="outlined-basic-email"
          fullWidth
          label="Type Something"
          variant="standard"
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          disabled={!ticket.ticket_id}
        />

        <IconButton
          disabled={(!message && !attachment) || loading}
          type="submit"
          color="primary"
          sx={{ flex: 1, maxWidth: "50px" }}
          aria-label="Send"
        >
          <SendIcon />
        </IconButton>

        <Label htmlFor="icon-button-file-AttachFileWithMessage">
          <IconButton
            onClick={() => {
              document
                .getElementById("icon-button-file-AttachFileWithMessage")
                ?.click();
            }}
            aria-label="Attach File"
          >
            <AttachFileIcon />
          </IconButton>
          <Input
            accept="image/*"
            id="icon-button-file-AttachFileWithMessage"
            type="file"
            onChange={attachFile}
          />
        </Label>
      </Box>
      {attachment && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            position: "relative",
            width: "fit-content",
          }}
        >
          <img
            style={{
              width: "100%",
              maxWidth: "50px",
              height: "50px",
              objectFit: "cover",
              borderRadius: "8px",
              marginTop: "10px",
            }}
            src={URL.createObjectURL(attachment)}
            alt={attachment.name}
          />
          <IconButton
            sx={{
              position: "absolute",
              top: "-5px",
              right: "-15px",
            }}
            onClick={() => setAttachment(null)}
            color="error"
            aria-label="remove attachment"
          >
            <RemoveCircleIcon />
          </IconButton>
        </Box>
      )}
    </form>
  );
}
